import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Shield, Heart, Cpu, ArrowRight, MessageSquare } from 'lucide-react';
import SpotlightCard from '../ui/SpotlightCard';
import AnimatedBadge from '../ui/AnimatedBadge';

export default function WhyUsSection() {
  const differentials = [
    {
      icon: Cpu,
      title: "IA + Humanidade",
      desc: "Deixamos robôs trabalharem por nós para trazermos mais qualidade de vida e atenção humanizada ao 'teti a teti' com você."
    },
    {
      icon: Shield,
      title: "Ética Inegociável",
      desc: "Praticamos a Elisão Fiscal (legal) e abominamos a Evasão. Honestidade faz parte do nosso DNA desde a fundação."
    },
    {
      icon: Zap,
      title: "Ecossistema Onvio",
      desc: "Foco total no Portal do Cliente e Onvio Messenger (WhatsApp), com mais de 25 tarefas automatizadas sem intervenção manual."
    },
    {
      icon: Heart,
      title: "Mundo Mais Humano",
      desc: "Acreditamos em uma sociedade mais justa e honesta. Somos uma empresa feita por pessoas, para pessoas."
    }
  ];

  return (
    <section id="why-us" className="w-full bg-background py-24 border-t border-foreground/5 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[1px] bg-gradient-to-r from-transparent via-orange-500/50 to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center lg:items-end mb-16 md:mb-24">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="mb-4 md:mb-6 text-center lg:text-left"
          >
            <AnimatedBadge>
              Nosso DNA
            </AnimatedBadge>
            <h2 className="text-2xl md:text-5xl font-black text-foreground tracking-tight leading-[1.4] mt-4 md:mt-6">
              Vemos o Mundo <br /> Diferente.
            </h2>
          </motion.div>
          <motion.p 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="text-sm md:text-xl text-slate-400 font-medium leading-relaxed max-w-xl text-center lg:text-left mx-auto lg:mx-0 px-4 md:px-0"
          >
            Acreditamos na integração de meta dados com atendimento humanizado, superando expectativas através da inovação.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {differentials.map((item, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className="h-full"
            >
              <SpotlightCard className="h-full" innerClassName="p-8 md:p-10">
                <div className="flex flex-col items-center md:items-start h-full text-center md:text-left">
                  <div className="w-14 h-14 bg-foreground/5 rounded-2xl flex items-center justify-center mb-6 md:mb-8 group-hover:scale-110 group-hover:bg-orange-600 transition-all duration-500">
                    <item.icon size={28} className="text-orange-500 group-hover:text-white transition-colors" />
                  </div>
                  <h4 className="text-xl font-bold text-foreground mb-4 tracking-tight group-hover:text-orange-500 transition-colors">{item.title}</h4>
                  <p className="text-slate-500 text-sm leading-relaxed flex-1">{item.desc}</p>
                </div>
              </SpotlightCard>
            </motion.div>
          ))}
        </div>

        {/* Tech Partner Bar */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-24"
        >
          <SpotlightCard innerClassName="p-10 flex flex-col md:flex-row items-center justify-between gap-10">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-foreground/5 rounded-2xl flex items-center justify-center">
                <MessageSquare className="text-orange-500" />
              </div>
              <div>
                <h5 className="text-foreground font-bold text-lg">Onvio Messenger</h5>
                <p className="text-slate-500 text-sm">+25 tarefas automatizadas via WhatsApp.</p>
              </div>
            </div>
            <div className="h-px w-20 bg-white/10 hidden md:block"></div>
            <div className="flex items-center gap-4">
              <span className="text-slate-400 text-xs font-bold uppercase tracking-widest">Powered by</span>
              <span className="text-foreground font-black text-xl tracking-tighter">Thomson Reuters</span>
            </div>
            <a 
              href="https://onvio.com.br/clientcenter/pt/auth?r=%2Fhome"
              rel="noopener noreferrer"
              className="w-full sm:w-[240px] px-10 py-4 bg-orange-600 hover:bg-orange-500 text-white font-black rounded-full transition-all duration-300 uppercase tracking-widest text-[10px] flex items-center justify-center gap-2 whitespace-nowrap"
            >
              Ver Portal do Cliente
              <ArrowRight size={16} />
            </a>
          </SpotlightCard>
        </motion.div>
      </div>
    </section>
  );
}
